import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import {
  Box,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';

import QRCodeColorPicker from '../QRCodeColorPicker';
import QrNonogramPromo from './QrNonogramPromo';
import { fetchGamePreviewKey } from '@shared/utils/game-preview';

import styles from './qr-code-generator.module.scss';

interface QRCodeGeneratorProps {
  gameUrl: string;
  gameBuildId?: string;
  showGameLink: boolean;
  isPaid?: boolean;
  ownerKey?: string;
}

const QRCodeGenerator: FC<QRCodeGeneratorProps> = ({
  gameUrl,
  gameBuildId,
  showGameLink,
  isPaid = true,
  ownerKey,
}) => {
  const { t } = useTranslation();
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');
  const [resolvedGameUrl, setResolvedGameUrl] = useState('');
  const [backgroundColor, setBackgroundColor] = useState<string | null>(null); // null = прозрачный
  const [foregroundColor, setForegroundColor] = useState<string>('#000000'); // Цвет QR-кода

  const generateQRCode = useCallback(async (url: string, bgColor: string | null = null, fgColor: string = '#000000') => {
    if (!url) return;

    setIsGenerating(true);
    setError('');

    try {
      // Создаем временный canvas для обработки
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 300;
      tempCanvas.height = 300;

      // Генерируем QR-код на временном canvas
      await QRCode.toCanvas(tempCanvas, url, {
        width: 300,
        margin: 2,
        color: {
          dark: fgColor,
          light: bgColor || '#FFFFFF',
        },
        errorCorrectionLevel: 'M',
      });

      const ctx = tempCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('Не удалось получить контекст canvas');
      }

      // Если выбран прозрачный фон, делаем белые пиксели прозрачными
      if (bgColor === null) {
        const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = imageData.data;
        const bgRgb = hexToRgb('#FFFFFF'); // Фон по умолчанию белый

        // Проходим по всем пикселям и делаем белые пиксели прозрачными
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          
          // Если пиксель белый (или почти белый), делаем его прозрачным
          if (bgRgb && r > 250 && g > 250 && b > 250) {
            data[i + 3] = 0; // Устанавливаем альфа-канал в 0 (прозрачный)
          }
        }

        ctx.putImageData(imageData, 0, 0);
      }

      // Конвертируем canvas в data URL
      const dataUrl = tempCanvas.toDataURL('image/png');
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Ошибка генерации QR-кода:', err);
      setError(t('qrCode.generationError') || 'Ошибка генерации QR-кода');
    } finally {
      setIsGenerating(false);
    }
  }, [t]);

  // Функция для конвертации hex в RGB
  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;

    const link = document.createElement('a');
    link.download = `qr-code-${gameBuildId || 'game'}.png`;
    link.href = qrCodeDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    let active = true;
    if (isPaid !== false) {
      setResolvedGameUrl(gameUrl);
      return () => { active = false; };
    }

    setResolvedGameUrl('');
    if (!gameUrl) return () => { active = false; };

    try {
      const sourceUrl = new URL(gameUrl, window.location.origin);
      const buildId = gameBuildId || sourceUrl.searchParams.get('buildId') || '';
      let editorKey = ownerKey || sourceUrl.searchParams.get('key') || '';
      if (!editorKey && buildId) {
        try { editorKey = localStorage.getItem(`game_preview_key_${buildId}`) || ''; } catch { /* Storage may be unavailable. */ }
      }
      if (!buildId || !editorKey) return () => { active = false; };

      void fetchGamePreviewKey(buildId, editorKey).then((previewKey) => {
        if (!active) return;
        sourceUrl.searchParams.set('buildId', buildId);
        sourceUrl.searchParams.set('key', previewKey);
        setResolvedGameUrl(sourceUrl.toString());
      }).catch(() => {
        if (active) setError(t('qrCode.generationError') || 'Ошибка генерации QR-кода');
      });
    } catch {
      setError(t('qrCode.generationError') || 'Ошибка генерации QR-кода');
    }

    return () => { active = false; };
  }, [gameUrl, gameBuildId, ownerKey, isPaid, t]);

  useEffect(() => {
    if (resolvedGameUrl) {
      // Небольшая задержка, чтобы убедиться, что canvas готов
      const timer = setTimeout(() => {
        generateQRCode(resolvedGameUrl, backgroundColor, foregroundColor);
      }, 100);
      return () => clearTimeout(timer);
    }
    setQrCodeDataUrl('');
  }, [resolvedGameUrl, backgroundColor, foregroundColor, generateQRCode]);

  // Показываем всегда, даже если игра еще не создана
  // if (!showGameLink || !gameUrl) {
  //   return null;
  // }

  return (
    <Box className={styles.qrCodeContainer}>
      <Paper 
        elevation={3} 
        className={styles.qrCodePaper}
        sx={{
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
        }}
      >
        <Box className={styles.qrCodeHeader}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <QrCodeIcon sx={{ color: '#1976d2', fontSize: '1.5rem' }} />
            <Typography variant="h6" className={styles.qrCodeTitle}>
              {t('qrCode.title') || 'QR-код для игры'}
            </Typography>
          </Stack>
          <Tooltip title={t('qrCode.refresh') || 'Обновить QR-код'}>
            {/* span: у disabled-кнопки Tooltip не получает события (предупреждение MUI) */}
            <span>
              <IconButton
                onClick={() => generateQRCode(resolvedGameUrl, backgroundColor, foregroundColor)}
                disabled={isGenerating || !resolvedGameUrl}
                size="small"
                sx={{ color: '#1976d2' }}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box className={styles.qrCodeContent}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <AlertTitle>{t('qrCode.error') || 'Ошибка'}</AlertTitle>
              {error}
            </Alert>
          )}

          {isGenerating ? (
            <Box className={styles.loadingContainer}>
              <Typography variant="body2" color="text.secondary">
                {t('qrCode.generating') || 'Генерируем QR-код...'}
              </Typography>
            </Box>
          ) : qrCodeDataUrl ? (
            <Stack spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 2,
                  backgroundColor: backgroundColor || 'white',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  display: 'inline-block',
                  // Шахматный фон для визуализации прозрачности (только если фон прозрачный)
                  ...(backgroundColor === null && {
                    backgroundImage: `
                      linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                      linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                      linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                      linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                    `,
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                  }),
                }}
              >
                <img
                  src={qrCodeDataUrl}
                  alt="QR-код для игры"
                  className={styles.qrCodeImage}
                />
              </Box>
              
              {/* Компонент выбора цвета фона и QR-кода */}
              <QRCodeColorPicker
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
                onBackgroundColorSelect={setBackgroundColor}
                onForegroundColorSelect={setForegroundColor}
              />
              
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadQRCode}
                  className={styles.downloadButton}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  {t('qrCode.download') || 'Скачать QR-код'}
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" textAlign="center">
                {t('qrCode.scanHint') || 'Отсканируйте QR-код для быстрого доступа к игре'}
              </Typography>
            </Stack>
          ) : (
            <Box className={styles.emptyContainer}>
              <Typography variant="body2" color="text.secondary">
                {t('qrCode.noData') || 'QR-код не сгенерирован'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Скрытый canvas для генерации */}
        <canvas
          ref={canvasRef}
          style={{ display: 'none' }}
          width={256}
          height={256}
        />
      </Paper>

      <QrNonogramPromo variant="accent" />
    </Box>
  );
};

export default QRCodeGenerator;
